"use client";

import { useState } from "react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import TableFormatter from "@/components/organisms/admin/TableFormatter";
import { Button } from "@/components/atoms/Button";
import ConfirmFieldChangeDialog, {
  type PendingFieldChange,
} from "@/components/molecules/admin/ConfirmFieldChangeDialog";
import { DEFAULT_ADMIN_FILE_INPUT_ACCEPT } from "@/constants/fileUpload";
import { FormProps } from "./types";
import { useForm1 } from "./hooks";

/**
 * A field opts into a blocking confirmation by declaring `confirmOnChange` in its field
 * config. Anything without it saves exactly as before.
 */
type ConfirmOnChange = {
  message?: string;
  note?: string;
  /** Pretty names per stored value, so the dialog shows "New ERP", not "new-erp". */
  optionLabels?: Record<string, string>;
};

/** Blank persisted value means "never set", which is the field's default — not a change. */
const effectiveValue = (raw: unknown, field: { defaultValue?: unknown }): string => {
  const s = raw == null ? "" : String(raw).trim();
  return s === "" ? String(field.defaultValue ?? "") : s;
};

const Form1 = ({
  selectedSection,
  onSave,
  pageId,
  pageComponentId,
  hasMetadata = true,
  dynamicOptions = {},
  variantDetails,
  fileAccept = DEFAULT_ADMIN_FILE_INPUT_ACCEPT,
}: FormProps) => {
  const {
    formState,
    currentVariantDetails,
    componentFieldsConfig,
    metadataFieldsWithOptions,
    handleComponentFieldsUpdate,
    handleMetadataUpdate,
    handleSave,
  } = useForm1({ selectedSection, hasMetadata, dynamicOptions, pageId, pageComponentId, variantDetails });

  const [pendingChanges, setPendingChanges] = useState<PendingFieldChange[]>([]);
  const [confirming, setConfirming] = useState(false);

  /**
   * Fields marked `confirmOnChange` whose value differs from what is currently SAVED
   * (`selectedSection.data`, i.e. the row as fetched — not the edit buffer). Empty for every
   * form that declares none, which is all of them except University Details today.
   */
  const collectPendingChanges = (): PendingFieldChange[] =>
    componentFieldsConfig
      .filter((field: any) => field.confirmOnChange)
      .map((field: any) => {
        const confirm: ConfirmOnChange = field.confirmOnChange;
        const from = effectiveValue(selectedSection.data?.[field.fieldName], field);
        const to = effectiveValue(formState.componentFields?.[field.fieldName], field);
        if (from === to) return null;
        const pretty = (v: string) => confirm.optionLabels?.[v] ?? v;
        return {
          fieldName: field.fieldName,
          label: field.label || field.fieldName,
          fromLabel: pretty(from),
          toLabel: pretty(to),
          message: confirm.message,
          note: confirm.note,
        } as PendingFieldChange;
      })
      .filter(Boolean) as PendingFieldChange[];

  const runSave = async () => {
    setConfirming(true);
    try {
      await handleSave(onSave);
    } finally {
      setConfirming(false);
      setPendingChanges([]);
    }
  };

  /** Save straight through unless a `confirmOnChange` field actually changed. */
  const handleSaveClick = () => {
    const changes = collectPendingChanges();
    if (changes.length > 0) {
      setPendingChanges(changes);
      return;
    }
    void runSave();
  };

  if (!currentVariantDetails) return null;

  return (
    <div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{currentVariantDetails.name}</h3>
        <p className="text-sm text-gray-500 mb-4">{currentVariantDetails.description}</p>
      </div>

      {componentFieldsConfig.length > 0 && (
        <div className="border-t pt-6 mb-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <span>📝</span> Content
          </h4>
          <FieldsFormatter
            data={formState.componentFields}
            fields={componentFieldsConfig}
            updateParent={true}
            onChange={handleComponentFieldsUpdate}
            validationErrors={formState.componentFieldErrors}
            validationMessages={formState.componentFieldMessages}
            className="p-0 bg-transparent"
            fileAccept={fileAccept}
          />
        </div>
      )}

      {metadataFieldsWithOptions.map((metaConfig) => (
        <div key={metaConfig.dataKey} className="border-t pt-6 mb-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <span>{metaConfig.icon}</span> {metaConfig.title}
          </h4>
          <TableFormatter
            data={formState.metadataStates[metaConfig.dataKey] || []}
            fields={metaConfig.fields}
            onDataUpdate={(updatedData) => handleMetadataUpdate(metaConfig.dataKey, updatedData)}
            validationErrors={formState.metadataErrors[metaConfig.dataKey] || {}}
            validationMessages={formState.metadataMessages[metaConfig.dataKey] || {}}
            fileAccept={fileAccept}
            minRows={metaConfig.minRows}
            maxRows={metaConfig.maxRows}
          />
        </div>
      ))}

      <div className="border-t pt-6 mb-6">
        <Button onClick={handleSaveClick} classType="admin" className="w-full">
          Save Widget Settings
        </Button>
      </div>

      <ConfirmFieldChangeDialog
        changes={pendingChanges}
        onConfirm={() => void runSave()}
        onCancel={() => setPendingChanges([])}
        isLoading={confirming}
      />
    </div>
  );
};

export default Form1;
